import {
  ChildFilterValue,
  FilterBuild,
  FilterBuildItemValue,
  ParentFilterValue
} from './types'
import Drawer from '@components/ui/Drawer'
import FiltersFooter from './FiltersFooter'
import FiltersHeader from './FiltersHeader'
import SavedFitBox from './SavedFitBox'
import RectangleOptions from './filters/RectangleOptions'
import SquareOptions from './filters/SquareOptions'
import Accordion from './FilterAccordion'
import styles from './styles.module.scss'
import { useContext } from 'react'
import CollectionContext from '../../collectionContext'
import { FilterOption } from 'types/filters'
import { useFit } from '@helpers/hooks/useFit'

export type FilterOptionFunction = (
  option: string
) => void | FilterOptionFunction

type FilterComponent =
  | typeof UnwrappedOptions
  | typeof SquareOptions
  | typeof RectangleOptions

function withAccordion(Component: FilterComponent) {
  return (props: ComponentWithAccordion) => {
    return (
      <Accordion title={props.title} active={props.active}>
        <Component {...props} onSelect={props.onSelect} />
      </Accordion>
    )
  }
}

type ComponentWithAccordion = {
  title: string
  active: boolean
  values: ParentFilterValue[] & ChildFilterValue[] & FilterBuildItemValue[]
  onSelect: FilterOptionFunction
}

const getDisplayTitle = (option: ParentFilterValue) => {
  const { title, selectedChildCount } = option

  return selectedChildCount && selectedChildCount > 0
    ? `${title} (${selectedChildCount})`
    : title
}

type UnwrappedOptionsProps = {
  title: string
  values: ParentFilterValue[] & ChildFilterValue[] & FilterBuildItemValue[]
  onSelect: FilterOptionFunction
}

const getComponentFromOptions = (
  option: ParentFilterValue,
  onSelect: FilterOptionFunction
) => {
  const Component = componentMap[option.type as keyof typeof componentMap]

  return (
    <Component
      title={getDisplayTitle(option)}
      key={option.title}
      values={
        option.values as ParentFilterValue[] &
          ChildFilterValue[] &
          FilterBuildItemValue[]
      }
      active={option.active}
      onSelect={onSelect(option.path) as FilterOptionFunction}
    />
  )
}

function UnwrappedOptions({ values, onSelect }: UnwrappedOptionsProps) {
  return <>{values.map(option => getComponentFromOptions(option, onSelect))}</>
}

export const componentMap = {
  default: UnwrappedOptions,
  accordion: withAccordion(UnwrappedOptions),
  'square-collapsible': withAccordion(SquareOptions),
  square: SquareOptions,
  'rectangle-collapsible': withAccordion(RectangleOptions)
}

function isFiltersWithActiveOptions(filters: FilterBuild) {
  return filters.some(filter =>
    filter?.selectedChildCount ? filter?.selectedChildCount > 0 : 0
  )
}

type FilterProps = {
  filters: FilterBuild
  selectedFilterOptions: FilterOption[]
  onFilterChange: FilterOptionFunction
  onFilterApply: () => void
  onFilterClear: () => void
  onApplyFitToggle: (state: boolean) => void
}
export default function Filters({
  filters,
  selectedFilterOptions,
  onFilterChange: handleFilterChange,
  onFilterApply: handleFilterApply,
  onFilterClear: handleFilterClear,
  onApplyFitToggle: handleApplyFit
}: FilterProps) {
  const {
    isFiltersOpen,
    isFitFilterToggleActive,
    setIsFitFilterToggleActive,
    closeFilters
  } = useContext(CollectionContext)
  const { savedFit } = useFit()
  const fit = savedFit || {}
  const hasActiveFilterOptions = isFiltersWithActiveOptions(filters)
  return (
    <Drawer
      size="medium"
      isDrawerOpen={isFiltersOpen}
      onCloseDrawer={closeFilters}
    >
      <section className={styles.filterContainer}>
        <FiltersHeader onClose={closeFilters} />
        <SavedFitBox
          savedFit={fit}
          onApplyFitToggle={handleApplyFit}
          isFitFilterToggleActive={isFitFilterToggleActive}
          setIsFitFilterToggleActive={setIsFitFilterToggleActive}
          selectedOptions={selectedFilterOptions}
        />
        <div className={styles.filters}>
          {filters.map(filterOption => {
            const Component =
              componentMap[filterOption.type as keyof typeof componentMap]

            return filterOption.type ? (
              <Component
                key={`${filterOption.type}-${filterOption.title}`}
                title={getDisplayTitle(filterOption)}
                active={filterOption.active}
                values={
                  filterOption.values as ParentFilterValue[] &
                    ChildFilterValue[] &
                    FilterBuildItemValue[]
                }
                onSelect={
                  handleFilterChange(filterOption.path) as FilterOptionFunction
                }
              />
            ) : null
          })}
        </div>
        <FiltersFooter
          onFilterApply={handleFilterApply}
          onFilterClear={handleFilterClear}
          isApplyFitActive={isFitFilterToggleActive}
          hasActiveFilterOptions={hasActiveFilterOptions}
        />
      </section>
    </Drawer>
  )
}
